import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { IMAGES } from "../lib/brand";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = "Choose a new password · Il Salotto Digitale"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password reset. Please sign in.");
      setTimeout(() => nav("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen pt-20 grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={IMAGES.interior} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute bottom-12 left-12 right-12 text-bg">
          <p className="text-[11px] uppercase tracking-ultra mb-4 text-bg/70">Il Salotto Digitale</p>
          <p className="font-serif text-4xl font-light leading-tight">Set a new password, and carry on.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:py-0">
        <div className="w-full max-w-md">
          {!token ? (
            <div data-testid="reset-invalid">
              <h1 className="font-serif font-light text-4xl text-ink tracking-tight">Invalid reset link</h1>
              <p className="mt-5 text-sm text-ink-soft font-light">This link is missing its token. Please request a new password reset.</p>
              <Link to="/forgot-password" data-testid="request-new-link" className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
                Request a new link <ArrowRight size={14} />
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <div className="w-14 h-14 border border-line flex items-center justify-center"><Check size={26} strokeWidth={1.4} className="text-ink" /></div>
              <h1 className="mt-8 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">Password reset</h1>
              <p className="mt-5 text-sm text-ink-soft font-light">Your password has been updated. Taking you to sign in…</p>
              <Link to="/login" data-testid="go-to-login" className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
                Sign in now <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-ultra text-accent">Member Area</p>
              <h1 className="mt-4 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">Choose a new password</h1>
              <p className="mt-4 text-sm text-ink-soft font-light">Enter a new password for your account below.</p>

              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reset-password-form">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">New Password</label>
                  <input data-testid="reset-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="••••••••" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Confirm Password</label>
                  <input data-testid="reset-confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="••••••••" />
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit"
                  className="group w-full bg-ink text-bg py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#33312E] transition-colors flex items-center justify-center gap-3 disabled:opacity-60">
                  {loading ? "Please wait…" : "Reset Password"}
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>

              <Link to="/login" className="mt-8 inline-block text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">← Back to sign in</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
